import React, {useState, useEffect} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import {
  getAllB2BAccounts,
  createB2BAccount,
  updateB2BAccount,
  sendB2BInvitation,
} from '../../../../redux/action/admin'
import {adminSwitchToAccount} from '../../../../redux/action/account'
import {generatePopup} from '../../../../utils/popup'
import STable from '../../../modules/cardform/components/helpers/STable'
import Modal from 'react-bootstrap/Modal'
import {validateEmail} from '../../../../_4threal/helpers'

interface Permission {
  billing: boolean
  user_management: boolean
  analytics: boolean
  experience_creation: boolean
  admin_experience_creation: boolean
  create_products_markers: boolean
}

const B2BAccountsTable: React.FC = () => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [modalType, setModalType] = useState('')
  const handleClose = () => setModalType('')
  const [selectedAccount, setSelectedAccount] = useState<any>(null)

  const [createForm, setCreateForm] = useState({
    businessName: '',
    adminToolsPermission: false,
  })
  const [createErrors, setCreateErrors] = useState<any>({})

  const [inviteForm, setInviteForm] = useState({
    businessId: '',
    email: '',
    role: 'sub-user' as 'primary' | 'sub-user',
    permissions: {
      billing: false,
      user_management: false,
      analytics: false,
      experience_creation: true,
      admin_experience_creation: false,
      create_products_markers: false,
    } as Permission,
  })
  const [inviteErrors, setInviteErrors] = useState<any>({})

  const {AllB2BAccounts: b2bAccounts} = useSelector((state: any) => state.Admin)
  const adminUserId = localStorage.getItem('userId')

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A'
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return date.toLocaleDateString()
    } catch {
      return 'N/A'
    }
  }
  // Transform data for STable
  const tableData = b2bAccounts.map((account: any) => ({
    id: account.id,
    businessName: account.businessName,
    status: account?.protected?.isActive ? 'Active' : 'Inactive',
    created: formatDate(account?.protected?.created),
    adminTools: account?.protected?.adminToolsPermission ? 'Yes' : 'No',
    userCount: account?.userCount || 0,
  }))

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true)
      await dispatch(getAllB2BAccounts({}))
      setIsLoading(false)
    }
    getData()
  }, [dispatch])

  const handleB2BAction = (type: string, action: string, selectedIds: string[]) => {
    if (action === 'create') {
      setModalType('create')
    } else if (action === 'edit' && selectedIds.length > 0) {
      const account = b2bAccounts.find((acc: any) => acc.id === selectedIds[0])
      if (account) {
        setSelectedAccount(account)
        setCreateForm({
          businessName: account.businessName,
          adminToolsPermission: account?.protected?.adminToolsPermission || false,
        })
        setModalType('edit')
      }
    } else if (action === 'invite' && selectedIds.length > 0) {
      const account = b2bAccounts.find((acc: any) => acc.id === selectedIds[0])
      if (account) {
        setSelectedAccount(account)
        setInviteForm({
          ...inviteForm,
          businessId: account.id,
        })
        setModalType('invite')
      }
    } else if (action === 'switch' && selectedIds.length > 0) {
      handleSwitchToAccount(selectedIds[0])
    } else if (action === 'toggle_active' && selectedIds.length > 0) {
      const account = b2bAccounts.find((acc: any) => acc.id === selectedIds[0])
      if (account) {
        handleToggleActive(account.id, account?.protected?.isActive)
      }
    } else if (action === 'toggle_admin' && selectedIds.length > 0) {
      const account = b2bAccounts.find((acc: any) => acc.id === selectedIds[0])
      if (account) {
        handleToggleAdminTools(account.id, account?.protected?.adminToolsPermission)
      }
    }
  }

  const handleCreateB2B = async () => {
    let errors: any = {}

    if (createForm.businessName.trim() === '') {
      errors.businessName = true
    }

    setCreateErrors(errors)
    if (Object.keys(errors).length === 0) {
      try {
        if (modalType === 'edit' && selectedAccount) {
          await dispatch(
            updateB2BAccount({
              accountId: selectedAccount.id,
              updatedData: {
                businessName: createForm.businessName,
                'protected.adminToolsPermission': createForm.adminToolsPermission,
              },
            })
          )
          generatePopup('success', 'B2B Account updated successfully!')
        } else {
          await dispatch(createB2BAccount(createForm))
          generatePopup('success', 'B2B Account created successfully!')
        }
        setModalType('')
        setCreateForm({businessName: '', adminToolsPermission: false})
        setSelectedAccount(null)
      } catch (error) {
        console.error('Failed to save B2B account:', error)
      }
    }
  }

  const handleSwitchToAccount = async (accountId: string) => {
    try {
      await dispatch(adminSwitchToAccount({targetAccountId: accountId}))
      window.location.href = '/dashboard'
    } catch (error) {
      console.error('Failed to switch to account:', error)
    }
  }

  const handleSendInvitation = async () => {
    let errors: any = {}

    const isEmailValid = validateEmail(inviteForm.email)
    if (!isEmailValid) {
      errors.email = true
    }

    setInviteErrors(errors)
    if (Object.keys(errors).length === 0) {
      try {
        await dispatch(
          sendB2BInvitation({
            businessId: inviteForm.businessId,
            email: inviteForm.email,
            role: inviteForm.role,
            permissions: inviteForm.permissions,
            invitedBy: adminUserId,
          })
        )
        setModalType('')
        setInviteForm({
          businessId: '',
          email: '',
          role: 'sub-user',
          permissions: {
            billing: false,
            user_management: false,
            analytics: false,
            experience_creation: true,
            admin_experience_creation: false,
            create_products_markers: false,
          },
        })
        generatePopup('success', 'Invitation sent successfully!')
      } catch (error) {
        console.error('Failed to send invitation:', error)
      }
    }
  }

  const handleToggleAdminTools = async (accountId: string, currentValue: boolean) => {
    try {
      await dispatch(
        updateB2BAccount({
          accountId,
          updatedData: {'protected.adminToolsPermission': !currentValue},
        })
      )
      dispatch(getAllB2BAccounts({})) // Refresh list
      generatePopup('success', 'Admin tools permission updated!')
    } catch (error) {
      console.error('Failed to update admin tools permission:', error)
    }
  }

  const handleToggleActive = async (accountId: string, currentValue: boolean) => {
    try {
      await dispatch(
        updateB2BAccount({
          accountId,
          updatedData: {'protected.isActive': !currentValue},
        })
      )
      dispatch(getAllB2BAccounts({})) // Refresh list
      generatePopup('success', 'Account status updated!')
    } catch (error) {
      console.error('Failed to update account status:', error)
    }
  }

  const handleChangeCreate = (e: any) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setCreateForm({...createForm, [e.target.id]: value})
  }

  const handleChangeInvite = (e: any) => {
    const value = e.target.value
    setInviteForm({...inviteForm, [e.target.id]: value})
  }

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (modalType === 'create' || modalType === 'edit') {
        handleCreateB2B()
      } else if (modalType === 'invite') {
        handleSendInvitation()
      }
    }
  }

  return (
    <>
      <STable
        headers={[
          {column: 'businessName', label: 'Business Name'},
          {column: 'status', label: 'Status'},
          {column: 'created', label: 'Created'},
          {column: 'adminTools', label: 'Admin Tools'},
          {column: 'userCount', label: 'Users'},
        ]}
        data={tableData}
        isLoading={isLoading}
        loadingTag={<h1>Loading...</h1>}
        handleReferral={handleB2BAction}
        type='b2b'
      />

      <Modal show={modalType !== ''} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === 'create'
              ? 'Create B2B Account'
              : modalType === 'edit'
              ? 'Edit B2B Account'
              : modalType === 'invite'
              ? `Invite User to ${selectedAccount?.businessName}`
              : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form style={{zIndex: 10000}}>
            {modalType === 'create' || modalType === 'edit' ? (
              <>
                <div className='row'>
                  <div className='col-sm'>
                    <div className='form-group mb-3'>
                      <label htmlFor='businessName'>Business Name*</label>
                      <input
                        type='text'
                        onChange={handleChangeCreate}
                        value={createForm.businessName}
                        className='form-control'
                        id='businessName'
                        aria-describedby='businessNameHelp'
                        placeholder='Enter Business Name'
                        onKeyDown={handleKeyDown}
                      />
                      {createErrors.businessName && (
                        <small id='businessNameHelp' style={{color: 'red'}} className='form-text'>
                          Business Name is required
                        </small>
                      )}
                    </div>
                  </div>
                </div>
                <div className='row'>
                  <div className='col-sm'>
                    <div className='form-group mb-3'>
                      <div className='form-check'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          checked={createForm.adminToolsPermission}
                          onChange={handleChangeCreate}
                          id='adminToolsPermission'
                        />
                        <label className='form-check-label' htmlFor='adminToolsPermission'>
                          Enable Admin Tools Permission
                        </label>
                      </div>
                      <div className='text-muted fs-7'>
                        This allows the business to use advanced experience building tools
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : modalType === 'invite' ? (
              <>
                <div className='row'>
                  <div className='col-sm'>
                    <div className='form-group mb-3'>
                      <label htmlFor='email'>Email Address*</label>
                      <input
                        type='email'
                        onChange={handleChangeInvite}
                        value={inviteForm.email}
                        className='form-control'
                        id='email'
                        aria-describedby='emailHelp'
                        placeholder='Enter Email Address'
                        onKeyDown={handleKeyDown}
                      />
                      {inviteErrors.email && (
                        <small id='emailHelp' style={{color: 'red'}} className='form-text'>
                          Enter a valid email address
                        </small>
                      )}
                    </div>
                  </div>
                </div>
                <div className='row'>
                  <div className='col-sm'>
                    <div className='form-group mb-3'>
                      <label htmlFor='role'>Role</label>
                      <select
                        onChange={handleChangeInvite}
                        name='role'
                        id='role'
                        value={inviteForm.role}
                        className='form-control'
                        onKeyDown={handleKeyDown}
                      >
                        <option value='sub-user'>Sub User</option>
                        <option value='primary'>Primary User</option>
                      </select>
                      <div className='text-muted fs-7 mt-2'>
                        Primary users have full control over the business account and can manage
                        other users.
                      </div>
                    </div>
                  </div>
                </div>
                <div className='row'>
                  <div className='col-sm'>
                    <div className='form-group mb-3'>
                      <label className='fw-semibold fs-6 mb-2'>Permissions</label>
                      <div className='row'>
                        {Object.entries(inviteForm.permissions).map(([key, value]) => (
                          <div key={key} className='col-md-6 mb-2'>
                            <div className='form-check'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                checked={value}
                                onChange={(e) =>
                                  setInviteForm({
                                    ...inviteForm,
                                    permissions: {
                                      ...inviteForm.permissions,
                                      [key]: e.target.checked,
                                    },
                                  })
                                }
                                disabled={inviteForm.role === 'primary'}
                                id={`permission-${key}`}
                              />
                              <label className='form-check-label' htmlFor={`permission-${key}`}>
                                {key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                      {inviteForm.role === 'primary' && (
                        <div className='alert alert-info mt-3'>
                          Primary users automatically receive all permissions.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-secondary' onClick={handleClose}>
            Cancel
          </button>
          <button
            type='button'
            className='btn btn-primary'
            onClick={modalType === 'invite' ? handleSendInvitation : handleCreateB2B}
          >
            {modalType === 'create'
              ? 'Create Account'
              : modalType === 'edit'
              ? 'Update Account'
              : modalType === 'invite'
              ? 'Send Invitation'
              : 'Save'}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default B2BAccountsTable
